import React from 'react';
import type { PodletResponse } from '../../server';

type AssetJs = PodletResponse['js'][0];

export function useReloadPodlet(
    jsAssets: AssetJs[],
    cautiouslyReloadScriptOnHydrationError: boolean,
) {
    const containerRef = React.useCallback(
        (element: HTMLElement | null) => {
            if (element !== null) {
                const didAttachShadow = element && reloadTemplates(element);
                if (didAttachShadow && cautiouslyReloadScriptOnHydrationError) {
                    jsAssets.forEach(reloadScript);
                }
            }
        },
        [cautiouslyReloadScriptOnHydrationError, jsAssets],
    );

    return { containerRef };
}

function reloadTemplates(root: Element | ShadowRoot): boolean {
    const templates = root.querySelectorAll('template[shadowrootmode]');

    let didAttachPodletDsdTemplate = false;

    templates.forEach((template) => {
        const templateParent = template.parentNode;

        if (templateParent) {
            let shadowRoot: ShadowRoot | null = null;
            try {
                shadowRoot = attachShadowRoot(template, templateParent);
                didAttachPodletDsdTemplate = true;
            } catch (err) {
                if (err instanceof DOMException) {
                    shadowRoot = handleExistingShadowRoot(
                        template,
                        templateParent,
                    );
                }
            }
            shadowRoot && reloadTemplates(shadowRoot);
        }
    });

    return didAttachPodletDsdTemplate;
}

function attachShadowRoot(
    template: Element,
    templateParent: ParentNode,
): ShadowRoot | null {
    const delegatesFocus = template.hasAttribute('shadowrootdelegatesfocus');
    const mode = template.getAttribute('shadowrootmode');

    if (!mode) {
        if (process.env.NODE_ENV === 'development') {
            console.info(
                `Missing shadow root mode for custom element: ${
                    (templateParent as Element).localName
                }`,
            );
        }
        return null;
    }

    const shadowRoot = (templateParent as Element).attachShadow({
        delegatesFocus,
        mode: mode as ShadowRootMode,
    });
    shadowRoot.appendChild((template as HTMLTemplateElement).content);
    template.remove();

    if (process.env.NODE_ENV === 'development') {
        console.info(
            `Attached shadow root for custom element: ${
                (templateParent as Element).localName
            }`,
        );
    }

    return shadowRoot;
}

function handleExistingShadowRoot(
    template: Element,
    templateParent: ParentNode,
): ShadowRoot | null {
    // Failed to attach DSD template to custom element that already
    // has shadow root. This probably means that the custom element
    // has a proper JS implementation that does not need the DSD.
    // In this case we just remove the DSD template and continue.
    template.remove();

    if (process.env.NODE_ENV === 'development') {
        console.info(
            `Found custom element with shadow root: ${
                (templateParent as Element).localName
            }`,
        );
    }

    const shadowRoot = (templateParent as Element).shadowRoot;
    const hasChildTemplates =
        (shadowRoot?.querySelectorAll('template[shadowrootmode]') ?? [])
            .length > 0;

    return hasChildTemplates ? shadowRoot : null;
}

function reloadScript(assetJs: AssetJs) {
    const scriptEle = document.createElement('script');
    scriptEle.crossOrigin = assetJs.crossorigin;
    scriptEle.defer = assetJs.defer;
    scriptEle.type = assetJs.type;

    document.body.appendChild(scriptEle);

    const reloadScriptUrl = assetJs.value ? `${assetJs.value}#reload` : '';
    scriptEle.src = reloadScriptUrl;
}
